<script setup lang="ts">
import { onMounted, onBeforeUnmount, watch } from "vue"

const props = defineProps<{
	active: boolean
	graphRef: any
	targetSelector: string
	zoom?: number
}>()

const zoomLevel = props.zoom ?? 2

let targetEl: HTMLElement | null = null
let savedViewport: { x: number; y: number; zoom: number } | null = null
let rafId = 0
let isZoomedIn = false

function findTarget() {
	targetEl = document.querySelector(props.targetSelector)
}

function onMouseMove(e: MouseEvent) {
	if (!props.active || !props.graphRef) return

	if (!targetEl || !document.body.contains(targetEl)) {
		findTarget()
	}
	if (!targetEl) return

	const rect = targetEl.getBoundingClientRect()
	const insideTarget =
		e.clientX >= rect.left && e.clientX <= rect.right &&
		e.clientY >= rect.top && e.clientY <= rect.bottom

	if (!insideTarget) {
		if (isZoomedIn) onMouseLeave()
		return
	}

	// Save the original viewport on first zoom
	if (!isZoomedIn) {
		savedViewport = { ...props.graphRef.getViewport() }
		isZoomedIn = true
	}

	// Convert screen cursor position to flow coordinates using the SAVED (original) viewport
	// This ensures stable coordinates regardless of the current zoom state
	const vp = savedViewport!
	const flowX = (e.clientX - rect.left - vp.x) / vp.zoom
	const flowY = (e.clientY - rect.top - vp.y) / vp.zoom

	// Set viewport zoomed in, centered on the cursor's flow position
	const centerX = rect.width / 2
	const centerY = rect.height / 2

	cancelAnimationFrame(rafId)
	rafId = requestAnimationFrame(() => {
		if (!props.graphRef) return
		props.graphRef.setViewport({
			x: centerX - flowX * zoomLevel,
			y: centerY - flowY * zoomLevel,
			zoom: zoomLevel,
		})
	})
}

function restoreViewport() {
	if (savedViewport && props.graphRef) {
		props.graphRef.setViewport(savedViewport, { duration: 200 })
		isZoomedIn = false
		savedViewport = null
	}
}

function onMouseLeave() {
	if (savedViewport && props.graphRef && isZoomedIn) {
		props.graphRef.setViewport(savedViewport, { duration: 200 })
		isZoomedIn = false
	}
}

watch(() => props.active, (val) => {
	if (!val) {
		restoreViewport()
	} else {
		findTarget()
	}
})

onMounted(() => {
	findTarget()
	document.addEventListener("mousemove", onMouseMove)
})

onBeforeUnmount(() => {
	document.removeEventListener("mousemove", onMouseMove)
	cancelAnimationFrame(rafId)
	restoreViewport()
})
</script>

<template>
	<!-- No visual element needed - this component controls VueFlow's viewport zoom -->
</template>
